import { Component, OnInit, OnDestroy, signal, computed, ChangeDetectionStrategy, inject } from '@angular/core';
import { Location, CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import * as XLSX from 'xlsx';
import { FirestoreProjectService } from '../../services/firestore-project.service';
import { AuthService } from '../../services/auth.service';
import { UserRole } from '../../models/user.model';
import { Project } from '../../models/project.model';

@Component({
    selector: 'app-priorities',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule],
    templateUrl: './priorities.component.html',
    styleUrls: ['./priorities.component.css']
})
export class PrioritiesComponent implements OnInit, OnDestroy {
    private allProjects     = signal<Project[]>([]);
    private authService     = inject(AuthService);
    private firestoreService = inject(FirestoreProjectService);
    private router          = inject(Router);
    private location        = inject(Location);
    private destroy$        = new Subject<void>();

    private isMinisterRole = computed<boolean>(() =>
        this.authService.currentUser()?.role === UserRole.MINISTER
    );

    priorityProjects = computed<Project[]>(() => {
        const projects = this.allProjects();
        if (this.isMinisterRole()) {
            return projects.filter(p => !!p.hmPriority);
        }
        return projects.filter(p =>
            !!p.hmPriority || !!p.recurringIntervention ||
            p.visibility === 'HM Committed' || p.visibility === 'High Visibility'
        );
    });

    // ── Filters ────────────────────────────────────────────────────────
    searchQuery     = signal('');
    filterStatus    = signal('');
    filterScheme    = signal('');
    filterBlock     = signal('');
    filterGP        = signal('');
    filterExecutant = signal('');
    cardView        = signal(true);
    currentPage     = signal(1);
    readonly pageSize = 8;

    availableSchemes    = computed(() => [...new Set(this.priorityProjects().map(p => p.scheme).filter((s): s is string => !!s))].sort());
    availableBlocks     = computed(() => [...new Set(this.priorityProjects().map(p => p.block).filter((s): s is string => !!s))].sort());
    availableGPs        = computed(() => [...new Set(this.priorityProjects().map(p => p.gp).filter((s): s is string => !!s))].sort());
    availableExecutants = computed(() => [...new Set(this.priorityProjects().map(p => p.executorName).filter((s): s is string => !!s))].sort());

    filteredProjects = computed<Project[]>(() => {
        let list = this.priorityProjects();
        const q  = this.searchQuery().toLowerCase().trim();
        const st = this.filterStatus();
        const sc = this.filterScheme();
        const bl = this.filterBlock();
        const gp = this.filterGP();
        const ex = this.filterExecutant();
        if (q)  list = list.filter(p => p.name.toLowerCase().includes(q) || (p.dept ?? '').toLowerCase().includes(q) || (p.loc ?? '').toLowerCase().includes(q));
        if (st) list = list.filter(p => p.status === st);
        if (sc) list = list.filter(p => p.scheme === sc);
        if (bl) list = list.filter(p => p.block === bl);
        if (gp) list = list.filter(p => p.gp === gp);
        if (ex) list = list.filter(p => p.executorName === ex);
        return list;
    });

    // ── Pagination ──────────────────────────────────────────────────────
    totalPages       = computed(() => Math.max(1, Math.ceil(this.filteredProjects().length / this.pageSize)));
    pages            = computed(() => Array.from({ length: this.totalPages() }, (_, i) => i + 1));
    pageStart        = computed(() => this.filteredProjects().length === 0 ? 0 : (this.currentPage() - 1) * this.pageSize + 1);
    pageEnd          = computed(() => Math.min(this.currentPage() * this.pageSize, this.filteredProjects().length));
    paginatedProjects = computed(() => {
        const start = (this.currentPage() - 1) * this.pageSize;
        return this.filteredProjects().slice(start, start + this.pageSize);
    });

    // ── Stats ───────────────────────────────────────────────────────────
    priStats = computed(() => {
        const p         = this.filteredProjects();
        const totalCost  = p.reduce((s, x) => s + (x.cost  ?? 0), 0);
        const totalSpent = p.reduce((s, x) => s + (x.spent ?? 0), 0);
        return {
            total:     p.length,
            totalCost,
            totalSpent,
            pctSpent:  totalCost > 0 ? Math.round((totalSpent / totalCost) * 100) : 0
        };
    });

    hasActiveFilters = computed(() =>
        !!this.searchQuery() || !!this.filterStatus() || !!this.filterScheme() ||
        !!this.filterBlock() || !!this.filterGP()     || !!this.filterExecutant()
    );

    showFilterPanel  = signal(false);
    toggleFilterPanel(): void { this.showFilterPanel.update(v => !v); }

    visiblePageNumbers = computed<number[]>(() => {
        const total   = this.totalPages();
        const current = this.currentPage();
        if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
        const result: number[] = [];
        const start = Math.max(1, current - 1);
        const end   = Math.min(total, current + 1);
        if (start > 1)          result.push(1);
        if (start > 2)          result.push(-1);
        for (let i = start; i <= end; i++) result.push(i);
        if (end < total - 1)    result.push(-1);
        if (end < total)        result.push(total);
        return result;
    });

    ngOnInit(): void {
        this.firestoreService.getProjects$()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next:  fs  => this.allProjects.set(fs),
                error: err => console.warn('[priorities] Firestore error:', err)
            });
    }
    ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }

    openProjectDetails(id: string): void { this.router.navigate(['/dhamnagar-dashboard/project', id]); }
    goBack():   void { this.location.back(); }
    goToHome(): void { this.router.navigate(['/home']); }
    goToPage(pg: number): void { this.currentPage.set(pg); }

    clearFilters(): void {
        this.searchQuery.set(''); this.filterStatus.set(''); this.filterScheme.set('');
        this.filterBlock.set(''); this.filterGP.set('');     this.filterExecutant.set('');
        this.currentPage.set(1);
    }

    exportToExcel(): void {
        const rows = this.filteredProjects().map((p, i) => ({
            '#': i + 1, 'Project Name': p.name, 'Department': p.dept,
            'Location': p.loc, 'Scheme': p.scheme ?? '', 'Block': p.block ?? '',
            'GP': p.gp ?? '', 'Cost (₹L)': p.cost, 'Spent (₹L)': p.spent,
            'Progress (%)': p.physical, 'Status': p.status,
            'HM Priority': p.hmPriority ? 'Yes' : 'No', 'Executant': p.executorName ?? '',
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'HM Priority Projects');
        XLSX.writeFile(wb, 'hm-priority-projects.xlsx');
    }

    getDeptAbbr(dept: string): string {
        const m: Record<string, string> = {
            'RD': 'RD', 'RWSS': 'RW', 'PR&DW': 'PR', 'Education': 'Ed',
            'Health': 'Ht', 'Irrigation': 'Ir', 'Housing': 'Ho',
            'Agriculture': 'Ag', 'Works': 'Wk', 'Energy': 'En',
        };
        return m[dept] ?? (dept ?? '??').substring(0, 2).toUpperCase();
    }

    getDeptColor(dept: string): string {
        const m: Record<string, string> = {
            'RD': '#2563eb', 'RWSS': '#0891b2', 'PR&DW': '#059669',
            'Education': '#7c3aed', 'Health': '#dc2626', 'Irrigation': '#0284c7',
            'Housing': '#d97706', 'Agriculture': '#16a34a', 'Works': '#ea580c', 'Energy': '#9333ea',
        };
        const fallbacks = ['#2563eb','#0891b2','#059669','#7c3aed','#dc2626','#d97706'];
        return m[dept] ?? fallbacks[(dept ?? '').charCodeAt(0) % fallbacks.length];
    }

    statusClass(s: string): string {
        const m: Record<string, string> = {
            'Completed': 'sc-done', 'In Progress': 'sc-ip', 'Stuck': 'sc-stuck', 'Planned': 'sc-plan'
        };
        return m[s] ?? 'sc-plan';
    }

    progressColor(pct: number): string {
        if (pct >= 75) return '#16a34a';
        if (pct >= 40) return '#2563eb';
        if (pct >= 20) return '#d97706';
        return '#dc2626';
    }

    fmt(val: number): string {
        if (!val) return '₹0';
        if (val >= 10000000) return `₹${(val / 10000000).toFixed(1)} Cr`;
        if (val >= 100000)   return `₹${(val / 100000).toFixed(2)} L`;
        return `₹${val.toLocaleString('en-IN')}`;
    }
}
